/*
 * Diamond Terrace — Landing Page behavior
 * - Scroll reveals (IntersectionObserver)
 * - Signup forms: intentionally NOT intercepted with JS/fetch. Shopify's
 *   {% form 'customer' %} posts to /contact, which is protected by
 *   CAPTCHA — Shopify's own script attaches a token to a genuine native
 *   form submission right before it goes out, but a token has no way to
 *   reach a form built manually from JS, so a fetch()-based submit always
 *   gets rejected ("Missing CAPTCHA token", confirmed against the live
 *   endpoint). The only reliable path is a real native submit, which is
 *   what happens here by default.
 *
 *   The confirmation state was originally meant to be rendered server-side
 *   in Liquid via form.posted_successfully? after Shopify redirects back
 *   (via the return_to field each form includes) — that part of the
 *   redirect works reliably. But when Shopify routes the submission through
 *   an hCaptcha /challenge page first (its spam-protection flow), the
 *   "posted successfully" state does not survive that extra hop: confirmed
 *   live that the customer IS actually created (shows up in Shopify
 *   Customers), but form.posted_successfully? still reads false on the
 *   page Shopify redirects back to. So instead: a sessionStorage flag is
 *   set synchronously the instant native submit fires (before the browser
 *   navigates away), and checked on every load — sessionStorage survives
 *   any number of same-origin redirect hops, unlike Liquid's form state,
 *   so it isn't affected by whether a challenge page was involved.
 *   form.posted_successfully? is left in place in the Liquid too, in case
 *   Shopify ever fixes that gap — belt and suspenders.
 *
 *   Landing position on that return trip is also forced explicitly (history
 *   .scrollRestoration = 'manual', plus an explicit scrollTo/scrollIntoView
 *   once the sessionStorage flag above is read) rather than trusted to the
 *   browser: removing our own id: param from the {% form %} tags stopped
 *   one source of an unwanted mid-page anchor jump on return, but not all
 *   of it, so the landing spot is pinned directly instead.
 * - Back to top button, appears once the footer section is in view.
 */
(function () {
  'use strict';

  // Confirmed root cause of the scroll jump: Shopify's {% form 'customer' %}
  // tag assigns the form a default id="contact_form" of its own — even with
  // no id: param supplied — and appends "#contact_form" to the action, so
  // the browser treats it as a same-page anchor to that form. That's why
  // forcing the scroll position after load wasn't enough on its own: the
  // browser can re-apply its native "scroll to the URL's fragment" behavior
  // *after* our own DOMContentLoaded scroll fix already ran (e.g. once
  // images/fonts finish loading and the layout shifts), silently winning
  // the race and undoing it. Stripping the fragment from the URL outright,
  // as early as this script can possibly run, removes the anchor before the
  // browser gets another chance to act on it — belt-and-suspenders with the
  // explicit scroll-forcing further down, which still runs too.
  if (location.hash && history.replaceState) {
    history.replaceState(null, '', location.pathname + location.search);
  }
  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }

  // ---------- Scroll reveals ----------
  function initReveals() {
    var els = document.querySelectorAll('.dt-reveal');
    if (!els.length) return;
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' });
    els.forEach(function (el) { io.observe(el); });
  }

  // ---------- Signup forms ----------
  // The form always submits natively — see the file header comment for why
  // it's never intercepted with preventDefault()/fetch(). Two things happen
  // around that native submit:
  var SIGNUP_STORAGE_KEY = 'dtSignupSuccess';

  function initSignupForms() {
    document.querySelectorAll('[data-dt-signup-group]').forEach(function (group) {
      var form = group.querySelector('form');
      if (!form) return;

      // 1. Mark which group is being submitted, synchronously, before the
      //    browser navigates away. sessionStorage survives every hop of
      //    Shopify's redirect chain (including an hCaptcha /challenge
      //    detour), so it's read reliably on whatever page/reload comes
      //    back — unlike Liquid's form.posted_successfully?, confirmed to
      //    NOT survive that chain when a challenge is involved.
      form.addEventListener('submit', function () {
        var submitBtn = form.querySelector('button[type="submit"]');
        if (submitBtn) submitBtn.disabled = true;
        try {
          sessionStorage.setItem(SIGNUP_STORAGE_KEY, group.getAttribute('data-dt-signup-group'));
        } catch (e) {
          // Storage unavailable (private browsing lockdown, etc.) — the
          // Liquid form.posted_successfully? path is still there as a
          // fallback for the (rarer) non-challenged submission case.
        }
      });
    });

    // 2. On every load, check for that flag. If it matches a group on this
    //    page, show its confirmation state and clear the flag so it
    //    doesn't reappear on a later, unrelated visit.
    var pending;
    try { pending = sessionStorage.getItem(SIGNUP_STORAGE_KEY); } catch (e) { pending = null; }
    if (!pending) return;

    var target = document.querySelector('[data-dt-signup-group="' + pending + '"]');
    if (target) {
      var formState = target.querySelector('[data-dt-signup-form]');
      var successState = target.querySelector('[data-dt-signup-success]');
      if (formState) formState.classList.add('is-hidden');
      if (successState) successState.classList.add('is-active');
    }
    try { sessionStorage.removeItem(SIGNUP_STORAGE_KEY); } catch (e) {}

    // Force the landing position instead of trusting whatever anchor/scroll
    // the browser wants to apply on this reload: the hero form lives at the
    // very top, so pin the page there; the footer form's confirmation is
    // far down the page, so bring it into view instead of leaving it
    // scrolled past. "auto" (not smooth) — this should look instant, like
    // the page simply loaded here, not like it animated into place.
    //
    // The join scroll fires at three moments (DOMContentLoaded, after a
    // short delay, and window load) because hero animations, video
    // preloading, and font/image reflows all cause layout shifts that can
    // silently override an earlier scrollIntoView.
    if (pending === 'hero') {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    } else if (target) {
      // target (data-dt-signup-group) has display:contents, so
      // getBoundingClientRect returns a zero-rect. Walk up to the
      // nearest ancestor with a real box (.dt-join) for scrolling.
      var scrollTarget = target.closest('.dt-join') || target;

      var userInteracted = false;
      var stopOnInteraction = function () { userInteracted = true; };
      window.addEventListener('touchstart', stopOnInteraction, { once: true, passive: true });
      window.addEventListener('wheel', stopOnInteraction, { once: true, passive: true });

      var pinToJoin = function () {
        var rect = scrollTarget.getBoundingClientRect();
        if (Math.abs(rect.top) > 2) {
          window.scrollTo(0, window.pageYOffset + rect.top);
        }
      };

      pinToJoin();
      var start = Date.now();
      (function enforce() {
        if (userInteracted || Date.now() - start > 2500) {
          window.removeEventListener('touchstart', stopOnInteraction);
          window.removeEventListener('wheel', stopOnInteraction);
          return;
        }
        pinToJoin();
        requestAnimationFrame(enforce);
      })();
    }
  }

  // ---------- Back to top ----------
  function initBackToTop() {
    var btn = document.getElementById('dtBackToTop');
    var target = document.querySelector('.dt-join');
    if (!btn || !target) return;

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          btn.classList.toggle('is-visible', entry.isIntersecting);
        });
      }, { threshold: 0.1 });
      io.observe(target);
    } else {
      btn.classList.add('is-visible');
    }

    btn.addEventListener('click', function () {
      var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    initReveals();
    initSignupForms();
    initBackToTop();
  });
})();
